// ANGEL FLIGHT — typed event bus (thermal AC-130 gunner).
//
// One global, strongly-typed pub/sub. Systems never import each other; they
// converse through this map. Backed by a tiny vanilla emitter (no Phaser, no
// engine dependency) so the whole logic layer stays renderer-agnostic and the
// pure-`node` tests run without a DOM.

import type { BodyZone, Difficulty, EnemyId, TracerStyle, Vec2, WeaponId, WorldPos } from './types';

export interface GunnerEvents {
  // — firing / combat —
  'weapon:fired': { weapon: WeaponId; at: Vec2; recoil: number };
  'weapon:spinup': { weapon: WeaponId; t: number };
  'weapon:dry': { weapon: WeaponId };
  'weapon:reloadStart': { weapon: WeaponId; durationMs: number };
  'weapon:reloadEnd': { weapon: WeaponId };
  /** The active weapon changed (keys 1-4). HUD reads this for the indicator. */
  'weapon:switched': { weapon: WeaponId };
  'ammo:changed': { ammo: number; cap: number };

  'hit:landed': {
    targetId: number;
    zone: BodyZone;
    damage: number;
    at: Vec2; // screen-space impact point
    /** True 3D world impact point (x lateral, z depth, y height) when known. */
    world?: WorldPos;
    /** Lateral shot direction at the target (-1..1) for directional flinch. */
    dirX?: number;
    kill: boolean;
  };

  // — enemies —
  'enemy:spawned': { id: number; def: EnemyId };
  'enemy:killed': {
    id: number;
    def: EnemyId;
    at: Vec2;
    world?: WorldPos;
    bounty: number;
    headshot: boolean;
  };

  // — friendly fire (AC-130 "DON'T HIT FRIENDLIES") —
  // A round landed on a BLUEFOR friendly. This is a PENALTY, not a kill: the
  // EconomySystem breaks the combo + docks SCRIP off this, and the lead flashes a
  // "CEASE FIRE" HUD banner + IFF marker. `at` is the screen impact; `world` the
  // 3D point when known; `dirX` the lateral shot direction for a flinch.
  'friendly:hit': { id: number; at: Vec2; world?: WorldPos; dirX?: number };
  // The PENULTIMATE friendly-fire strike was just counted — one more fails the
  // mission. The HUD shows the 'CHECK FIRE' callout and the audio layer sounds
  // the warning klaxon off this, so the final strike is never a surprise.
  'friendly:warning': { strikes: number; strikesLeft: number };
  // The friendly was downed by the gunner's fire (the render layer collapses the
  // BLUEFOR rig). Distinct from `enemy:killed` so it never pays out or juices a
  // kill — it reads as a casualty, not a frag.
  'friendly:down': { id: number; at: Vec2; world?: WorldPos };

  // — ranged enemies (the lead wires the visuals: a telegraph glow + an
  //   incoming-projectile tracer) —
  /** A ranged enemy began its wind-up tell BEFORE firing. `id` is the firing
   *  enemy; `from` is its world muzzle; `durationMs` is how long the tell lasts. */
  'enemy:telegraph': { id: number; from: WorldPos; durationMs: number };
  /** A ranged enemy released a projectile toward the gunner. `id` is a unique
   *  projectile id; `enemyId` is the shooter; `from` is the spawn world point;
   *  `lob` flags an arcing acid glob (vs. a flat rocket); `flareable` flags a
   *  heat-seeking rocket the HUD warns about (flares can decoy it). */
  'enemy:firedProjectile': { id: number; enemyId: number; from: WorldPos; toGunner: boolean; lob: boolean; flareable: boolean };
  /** An enemy projectile reached the strike plane and bit the bird. `damage` is
   *  the hull damage applied (the gunner has already taken it). */
  'projectile:hitGunner': { id: number; damage: number };
  /** A flare-able rocket in flight was DECOYED by the live flare window — it
   *  veers off harmlessly instead of biting the bird. `world` is where it died. */
  'projectile:decoyed': { id: number; world: WorldPos };

  // — countermeasures (key F) —
  /** The gunner popped a flare burst. The HUD starts its cooldown readout off
   *  this; the render layer may spark/flash off it. */
  'flare:deployed': { activeMs: number; cooldownMs: number };

  // — player —
  'hull:damaged': { hull: number; maxHull: number };
  'hull:down': Record<string, never>;

  // — juice (screen-space, render-only listeners) —
  'fx:shake': { trauma: number };
  'fx:hitstop': { ms: number };
  'fx:muzzle': { at: Vec2; angle: number };
  /** `toWorld` is the true 3D end point (preferred); `to` is the screen fallback.
   *  `style` lets the render layer draw the right line per weapon (the lead's
   *  follow-up reads it); absent = the default thin tracer. */
  'fx:tracer': { from: Vec2; to: Vec2; toWorld?: WorldPos; style?: TracerStyle };
  'fx:blood': { at: Vec2; scale: number; world?: WorldPos };
  'fx:hitmarker': { at: Vec2; kill: boolean; headshot: boolean };

  // — economy —
  'scrip:changed': { total: number; delta: number };
  'combo:changed': { mult: number; count: number };
  'salvage:changed': { total: number; delta: number };

  // — crew comms (render/audio-side: the CrewComms director authors these) —
  /** One crew radio line cleared the comms director (cooldowns + global gap).
   *  The HUD renders it on the comms ticker; the AudioBus fires the radio
   *  squelch blip. `urgent` lines style hotter (check-fire, hull critical). */
  'comms:line': { text: string; speaker: string; urgent: boolean };

  // — flow —
  /** A wave with an authored `banner` beat just fired ('CONTACT', 'BRUTE',
   *  'THE SURGE', …). The HUD renders it as a centre callout. */
  'wave:banner': { text: string };
  'objective:timer': { timeLeftMs: number; timeLimitMs: number };
  'mission:cleared': { missionId: string };
  'mission:failed': { missionId: string; reason: string };

  // — meta —
  'difficulty:changed': { difficulty: Difficulty };
  'save:written': Record<string, never>;
}

export type GunnerEventName = keyof GunnerEvents;
export type GunnerListener<K extends GunnerEventName> = (
  payload: GunnerEvents[K],
) => void;

/** One registered listener: the original fn (the identity used by `off`), the
 *  optional `this` context to bind, and whether it fires only once. */
interface Registration {
  fn: (payload: unknown) => void;
  ctx?: unknown;
  once: boolean;
}

/**
 * Minimal strongly-typed pub/sub. Drop-in for the previous Phaser-backed bus:
 * same `emit/on/once/off/removeAll` surface, same `ctx`-binding + `off`-by-fn
 * semantics. Listeners added during an `emit` are not invoked until the next
 * `emit` (we iterate a snapshot), matching the prior behaviour.
 */
export class TypedEmitter {
  private readonly map = new Map<string, Registration[]>();

  emit<K extends GunnerEventName>(event: K, payload: GunnerEvents[K]): void {
    const regs = this.map.get(event);
    if (!regs || regs.length === 0) return;
    // Snapshot so once-removal / re-entrant on() during dispatch is safe.
    for (const reg of regs.slice()) {
      if (reg.once) this.remove(event, reg.fn, reg.ctx);
      reg.fn.call(reg.ctx, payload);
    }
  }

  on<K extends GunnerEventName>(event: K, fn: GunnerListener<K>, ctx?: unknown): this {
    this.add(event, fn as Registration['fn'], ctx, false);
    return this;
  }

  once<K extends GunnerEventName>(event: K, fn: GunnerListener<K>, ctx?: unknown): this {
    this.add(event, fn as Registration['fn'], ctx, true);
    return this;
  }

  off<K extends GunnerEventName>(event: K, fn?: GunnerListener<K>, ctx?: unknown): this {
    if (!fn) {
      this.map.delete(event);
      return this;
    }
    this.remove(event, fn as Registration['fn'], ctx);
    return this;
  }

  removeAll(): void {
    this.map.clear();
  }

  private add(event: string, fn: Registration['fn'], ctx: unknown, once: boolean): void {
    const regs = this.map.get(event);
    const reg: Registration = { fn, ctx, once };
    if (regs) regs.push(reg);
    else this.map.set(event, [reg]);
  }

  private remove(event: string, fn: Registration['fn'], ctx: unknown): void {
    const regs = this.map.get(event);
    if (!regs) return;
    // Remove the first matching (fn, ctx) registration, mirroring EventEmitter.
    const i = regs.findIndex((r) => r.fn === fn && r.ctx === ctx);
    if (i >= 0) regs.splice(i, 1);
    if (regs.length === 0) this.map.delete(event);
  }
}

export function createEventBus(): TypedEmitter {
  return new TypedEmitter();
}
